// src/hooks/useCart.tsx
import {
  createContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import type { ReactNode } from "react";
import type { ID, CartItem } from "../../types";

/**
 * English comments only and concise.
 *
 * CartItem shape:
 *  - productId: ID
 *  - quantity: number
 *  - name: string
 *  - price: string
 */

/* Context shape */
type ContextValue = {
  items: CartItem[];
  currency: string;
  addToCart: (
    productId: ID,
    previewImage: string,
    quantity?: number,
    name?: string,
    price?: number
  ) => void;
  removeFromCart: (productId: ID, all?: boolean) => void;
  updateQuantity: (productId: ID, quantity: number) => void;
  clearCart: () => void;
  total: number; // numeric total computed from item.price strings
  itemCount: number;
  checkout: () => void;
  isProductInCart: (id: ID) => boolean;
};

export const CartContext = createContext<ContextValue | undefined>(undefined);

export interface CartProviderProps {
  children: ReactNode;
  currency?: string;
  initialItems?: CartItem[];
  persistKey?: string | null; // optional persistence key
  onCheckout?: (items: CartItem[]) => void;
}

/* Helper: parse price string to number safely */
/**function parsePriceString(priceStr: string): number {
  // normalize comma decimals, strip currency symbols and spaces
  const normalized = String(priceStr)
    .trim()
    .replace(/\s+/g, "")
    .replace(",", ".");
  const cleaned = normalized.replace(/[^0-9.\-]/g, "");
  const v = parseFloat(cleaned);
  return Number.isFinite(v) ? v : 0;
} */

export const CartProvider = ({
  children,
  initialItems = [],
  persistKey = "ragemp_cart",
  currency: currencyFromProps = "USD",
  onCheckout,
}: CartProviderProps) => {
  // load from localStorage if key provided
  const loadInitial = () => {
    if (typeof window === "undefined" || !persistKey) return initialItems;
    try {
      const raw = localStorage.getItem(persistKey);
      if (!raw) return initialItems;
      const parsed = JSON.parse(raw) as CartItem[];
      return Array.isArray(parsed) ? parsed : initialItems;
    } catch {
      return initialItems;
    }
  };

  const [items, setItems] = useState<CartItem[]>(() => loadInitial());

  const [currency] = useState<string>(currencyFromProps);

  // derived: total and itemCount
  const itemCount = useMemo(
    () => items.reduce((s, it) => s + it.quantity, 0),
    [items]
  );

  const total = useMemo(() => {
    // sum price parsed from strings multiplied by quantity
    return items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  }, [items]);

  // persist to localStorage when items change
  useEffect(() => {
    if (!persistKey) return;
    try {
      localStorage.setItem(persistKey, JSON.stringify(items));
    } catch {
      // ignore persistence errors
    }
  }, [items, persistKey]);

  // actions
  const addToCart = useCallback(
    (
      productId: ID,
      previewImage: string,
      quantity = 1,
      name?: string,
      price?: number
    ) => {
      setItems((prev) => {
        const idx = prev.findIndex((i) => i.productId === productId);
        if (idx === -1) {
          const newItem: CartItem = {
            productId,
            quantity,
            name: name ?? String(productId),
            price: price ?? 0,
            previewImage,
          };
          return [...prev, newItem];
        } else {
          const copy = prev.slice();
          copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
          return copy;
        }
      });
    },
    []
  );

  const removeFromCart = useCallback((productId: ID, all = false) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.productId === productId);
      if (idx === -1) return prev;
      if (all) return prev.filter((i) => i.productId !== productId);
      const copy = prev.slice();
      const item = copy[idx];
      const newQty = item.quantity - 1;
      if (newQty <= 0) return copy.filter((i) => i.productId !== productId);
      copy[idx] = { ...item, quantity: newQty };
      return copy;
    });
  }, []);

  const updateQuantity = useCallback((productId: ID, quantity: number) => {
    setItems((prev) => {
      if (quantity <= 0) return prev.filter((i) => i.productId !== productId);
      return prev.map((i) =>
        i.productId === productId ? { ...i, quantity } : i
      );
    });
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const isProductInCart = (id: ID) =>
    items.some((item) => item.productId === id);

  const checkout = useCallback(() => {
    if (onCheckout) onCheckout(items);
    setItems([]);
  }, [items, onCheckout]);

  const value: ContextValue = useMemo(
    () => ({
      items,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      total,
      isProductInCart,
      itemCount,
      checkout,
      currency,
    }),
    [
      items,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      total,
      itemCount,
      isProductInCart,
      checkout,
      currency,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
